<template>
<div class="table-container" :id="'widgetppt' + widget.id">
  <transition name="slide-fade" mode="out-in" v-if="!print">
    <b-button class="table-scroll-btn" variant="primary" @click="hideTableScrollBtn = false" v-if="hideTableScrollBtn">Scroll Right For Additional Info <i class="fa fa-arrow-right" aria-hidden="true"></i></b-button>
  </transition>

  <b-card no-body>
    <div class="card-header" :class="{fixedCardHeader: isOverflown}">
      <span v-b-tooltip.hover :title="title" class="widget-title">{{title}}</span>
      <settings-button />
    </div>
    <b-card-body class="p-3 clearfix" v-if='rows.length > 0' @click="handleClickThrough">

      <b-container v-if="!hideTableControls" fluid class='no-paddings'>
        <b-row>
          <b-col md="6">
            <b-input-group>
              <b-form-input v-model="filter" placeholder="Type to Search" />
              <b-input-group-append>
                <b-btn @click="filter = ''">Clear</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-col>

          <b-col md="6">
            <b-input-group prepend="Per page">
              <b-form-select :options="pageOptions" v-model="perPage" placeholder="5" />
            </b-input-group>
          </b-col>
        </b-row>
      </b-container>

      <b-table :id="`table-${id}`" :filter='filter' @filtered='onFiltered' :hover="hover" :striped="striped" :bordered="bordered" :small="small" :fixed="fixed" responsive="sm" :items="rows" :fields="fields" :current-page="currentPage" :per-page="perPage" :sort-by.sync="defaultSortColumn" :sort-desc.sync="defaultSortOrder">
        <template v-if="!compareRows && widget.options.include_totals_row" slot="top-row" slot-scope="data">
          <td v-for="(field, i) in fields" :key="field.key">
            <strong>{{ aggregateColumn(field.key, i) }}</strong>
          </td>
        </template>
        <template slot="created_at" slot-scope="data">
          {{ formatDate(data.value) }}
        </template>
        <template slot="updated_at" slot-scope="data">
          {{ formatDate(data.value) }}
        </template>
        <template slot="Date" slot-scope="data">
          {{ formatDate(data.value) }}
        </template>
        <template slot="profile_photo" slot-scope="data">
          <span v-html="data.value"></span>
        </template>
        <template v-for="field in fields" :slot="field.key" slot-scope="data">
          <CompareValues :key="field.key" :label="field.key" v-if="(!isNaN(data.value) || checkNumber(data.value)) && compareRows && data.value != null" :currentValue="data.value" :oldValue="calcOldRows(compareRows, data)" />
          <a v-if="field.key === 'audio_file_link' && data.value" :key="field.key" v-bind:href="data.value" target='_blank'>listen</a>
          <a v-else-if="data.value && data.value.id && field.key === 'campaign id'" :key="field.key" :href="'https://campaigns.webstreak.com/adworks_orders/' + data.value.adworks_order_id" target='_blank'>{{ data.value.id }}</a>
          <span v-else-if="isNaN(data.value) && !checkNumber(data.value)  || !compareRows" :key="field.key">{{roundData(data.value, field.key)}} </span>
        </template>
        <template slot="name" slot-scope="data">
          <span v-html="data.value"></span>
        </template>
        <template slot="ad_preview" slot-scope="data">
          <div @click="$eventBus.$emit('previewAd', data.value)" style="cursor: pointer;">Preview Ad</div>
        </template>
        <template slot="ad_preview_url" slot-scope="data">
          <div @click="$eventBus.$emit('previewAd', data.value)" style="cursor: pointer;">Preview Ad</div>
        </template>
      </b-table>
      <b-nav v-if="!print && !hideTableControls">
        <b-pagination size="sm" :total-rows="totalRows" :per-page="perPage" v-model="currentPage" prev-text="Prev" next-text="Next" hide-goto-end-buttons/>
        <div class="table-count">{{ totalRows }} of {{ rows.length }}</div>
      </b-nav>
    </b-card-body>
    <b-card-body class="p-3 clearfix" v-else>
      <h5 class="loading-text">{{ loadMessage }}</h5>
    </b-card-body>
    <div class="comparison-disclaimer" v-if="this.widget.disableDataComparison"><sup>*</sup>Data comparison unavailable</div>
  </b-card>
</div>
</template>

<script>
import { base_widget } from '@/views/widgets/base_widget'
import Moment from 'moment'
import CompareValues from '@/views/base/CompareValues'

export default {
  name: 'TableWidget',
  mixins: [base_widget],
  components: { CompareValues },
  data () {
    return {
      defaultSortColumn: null,
      defaultSortOrder: null,
      rows: [],
      compareRows: null,
      fields: [],
      currentPage: 1,
      perPage: 5,
      totalRows: 0,
      filter: null,
      pageOptions: [ 5, 10, 15, 20, 50, 100 ],
      hideTableScrollBtn: false,
      loadMessage: 'Loading...',
      filteredRows: [],
      weightedColumns: ['session_duration', 'search_impression_share', 'search_budget_lost_impression_share', 'search_rank_lost_impression_share', 'absolute_top_impression_percentage', 'top_impression_percentage', 'impression_lost_to_budget_percent'],
      sumColumns: ['sessions', 'pageviews', 'bounces', 'clicks', 'impressions', 'cost', 'spend', 'conversions', 'views', 'reach'],
      impressionWeightedColumns: ['search_impression_share', 'search_budget_lost_impression_share', 'search_rank_lost_impression_share', 'absolute_top_impression_percentage', 'top_impression_percentage', 'impression_lost_to_budget_percent']

    }
  },

  props: {
    hover: {
      type: Boolean,
      default: true
    },
    striped: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    }
  },

  created () {
    if (this.widget.options.default_per_page) {
      this.perPage = parseInt(this.widget.options.default_per_page)
    }
  },

  methods: {
    roundData (value, key) {
      var result
      if (!isNaN(value) && value !== null) {
        if (value.toString().indexOf('.') != -1) {
          try {
            result = value.toFixed(2)
          }
          catch(err) {
            result = parseFloat(value).toFixed(2)
          }
        } else {
          result = value
        }
        var currencyColumns = ['average_cpc', 'cost', 'spend', 'advertiser_cost', 'partner_cost', 'cpc', 'cost_per_conversion', 'calendar_budget', 'budget']
        if (currencyColumns.includes(key)) {
          result = this.formatCurrency(result)
        }
      } else {
        if (Array.isArray(value)){
          result = value[0]['value']
        } else {
          result = value
        }
      }
      return result
    },
    setData (response){
      var data = response.data.data
      var sorting = data.sorting
      if (sorting && sorting.default_sort_column){
        this.defaultSortColumn = sorting.default_sort_column
        this.defaultSortOrder = this.setSortOrder(sorting.default_sort_order)
        this.compareRows = null
      }
      if (data.value) {
        if (data.value.widget_titles) {
          this.fields = this.addSortable(data.value.table_columns, data.value.widget_titles)
        } else {
          this.fields = this.addSortable(data.value.table_columns)
        }
        this.rows = data.value.rows
        this.compareRows = data.compare_value.rows

        this.rows = this.rows.map( (row, index) => {
          let tmp = row
          if(this.compareRows[index]) {
            tmp.compare = this.compareRows[index]
          } else {
            tmp.compare = []
          }
          return tmp
        } )

        this.saveWidgetData(this.rows, this.fields)
      } else {
        if (data.widget_titles) {
          this.fields = this.addSortable(data.table_columns, data.widget_titles)
        } else {
          this.fields = this.addSortable(data.table_columns)
        }
        this.rows = data.rows
        this.saveWidgetData(this.rows, this.fields)
        setTimeout(() => { this.scrollAlert() }, 1000)
      }
      if (this.rows.length === 0) {
        this.loadMessage = 'No Data Available'
        this.saveWidgetData({}, [])
      } else {
        this.rows = this.forceLowerCaseLabels(this.rows)
      }

      if (this.print) {
        this.perPage = this.totalRows
        this.$eventBus.$emit('updateWidgetHeight',this.id, this.rows.length)
      }
      if (this.compareRows) {
        this.compareRows = this.forceLowerCaseLabels(this.compareRows)
      }
    },

    calcOldRows (compareRows, data) {
      if (data.item.compare && data.item.compare[data.field.key]) {
        return data.item.compare[data.field.key]
      } else {
        return 0
      }
    },

    forceLowerCaseLabels (rows) {
      var newRows = rows.map(function(row){
        var newRow = {}
        Object.keys(row).forEach(function(key){
          newRow[key.toLowerCase()] = row[key]
        })
        return newRow
      })
      return newRows
    },

    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.filteredRows = filteredItems
      this.currentPage = 1
    },

    addSortable (columns, titles = []) {
      var sortableColumns = []
      if (titles.length > 0) {
        columns.forEach(function(column, index){
        sortableColumns.push({key: column.toLowerCase(), label: titles[index],  sortable: true })
      }.bind(this))
      } else {
        columns.forEach(function(column){
          sortableColumns.push({key: column.toLowerCase(), sortable: true })
        }.bind(this))
      }
      return sortableColumns
    },

    scrollAlert() {
      var $table = document.getElementById(`table-${this.id}`)
      if ($table !== null){
        if($table.offsetWidth > $table.parentNode.offsetWidth){
          this.hideTableScrollBtn = true
        }
      }
    },

    formatDate (value) {
      if (~value.indexOf('T')) {
        return Moment(value).format('ll h:mm A')
      } else if (value) {
        return Moment(value).format('ll')
      }
    },

    formatCurrency (value) {
      var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      })
      return formatter.format(value)
    },

    setSortOrder (order) {
      return order.toLowerCase() === 'desc'
    },

    isObject (val) {
      return val instanceof Object;
    },

    aggregateColumn (key, i) {
      if (i == 0) {
        return 'Totals'
      }

      var rows
      var percentageRegex = /(\d+\.\d+)%/g
      var currencyRegex = /\$(\d+\.\d+)/g
      var timeRegex = /\d{2}:\d{2}:\d{2}/g
      var cost

      // live update based on filter
      if (this.filteredRows.length > 0) {
        rows = this.filteredRows
      } else {
        rows = this.rows
      }

      var items = rows.map(function(row){
        if (isNaN(row[key]) && typeof row[key] !== 'undefined') { // handle strings
          var mappedRow = {}
          var trimmedValue
          if (row[key].match(percentageRegex) && row[key].match(percentageRegex).length > 0) {
            trimmedValue = parseFloat(row[key].match(percentageRegex)[0]) // trim % from string
            if (this.impressionWeightedColumns.includes(key) && this.keys.includes('impressions')) { // use mappedRow for weighted average
              mappedRow.reference = row['impressions']
              mappedRow.value = trimmedValue
              return mappedRow
            } else {
              return trimmedValue
            }
          } else if (row[key].match(timeRegex)) { // parse time string & calculate total seconds
            var moment = Moment(row[key], 'HH:mm:ss')
            var hours = parseInt(moment.format('HH'))
            var minutes = parseInt(moment.format('mm'))
            var seconds = parseInt(moment.format('ss'))
            var totalSeconds = (hours * 3600) + (minutes * 60) + seconds
            if (key == 'session_duration') { // use mappedRow for weighted average
              mappedRow.reference = row['sessions']
              mappedRow.value = totalSeconds
              return mappedRow
            } else {
              return totalSeconds
            }
          } else if (row[key].match(currencyRegex)) {
            trimmedValue = parseFloat(row[key].match(currencyRegex)[0]) // trim $ from string
            return trimmedValue
          } else {
            return parseFloat(row[key])
          }
        } else { // handle numbers
          return parseFloat(row[key])
        }
      }.bind(this))

      var value
      if (this.sumColumns.includes(key)) { // plain old sum
        value = this.sumItems(items)
        return this.roundData(value, key)
      } else if (['avg_position', 'duration_seconds'].includes(key)) { // plain old average
        value = this.averageItems(items)
        value = this.roundData(value, key)
        if (key == 'duration_seconds') {
          value = this.formatSeconds(value)
        }
        return value
      } else if (this.weightedColumns.includes(key)){ // weighted averages
        var total
        if (key == 'session_duration' && this.keys.includes('sessions')) {
          total = this.aggregateColumn('sessions')
        } else if (this.impressionWeightedColumns.includes(key) && this.keys.includes('impressions')) {
          total = this.aggregateColumn('impressions')
        } else { // return simple average if weight columns not found
          value = this.averageItems(items)
          if (key == 'session_duration') {
            return this.formatSeconds(value)
          } else {
            return this.roundData(value, key) + '%'
          }
        }
        var weightedAvg = 0
        items.forEach(function(row) {
          if (typeof row !== 'undefined' && !isNaN(row.value)) {
            weightedAvg += (row.value * row.reference / total)
          }
        })
        if (key == 'session_duration') {
          value = this.formatSeconds(weightedAvg)
        } else {
          value = this.roundData(weightedAvg, key) + '%'
        }
        return value
      } else if (['ctr'].includes(key)) {
        var ctr
        if (this.keys.includes('clicks') && this.keys.includes('impressions')) {
          var clicks = this.aggregateColumn('clicks')
          var impressions = this.aggregateColumn('impressions')
          ctr = clicks / impressions * 100
        } else { // return simple average if reference columns not found
          ctr = this.averageItems(items)
        }
        return this.roundData(ctr, key) + '%'
      } else if (['cpc', 'average_cpc'].includes(key)) {
        var cpc
        if (this.keys.includes('cost') && this.keys.includes('clicks')) {
          cost = this.aggregateColumn('cost')
        } else if (this.keys.includes('spend') && this.keys.includes('clicks')) {
          cost = this.aggregateColumn('spend')
        } else { // return simple average if reference columns not found
          cpc = this.averageItems(items)
          return this.roundData(cpc, key)
        }
        var currency = Number(cost.replace(/[^0-9.-]+/g,""))
        cpc = currency / this.aggregateColumn('clicks')
        return this.roundData(cpc, key)
      } else if (['conversion_rate'].includes(key)) {
        var conv_rate
        if (this.keys.includes('conversions') && this.keys.includes('clicks')) {
          conv_rate = this.aggregateColumn('conversions') / this.aggregateColumn('clicks') * 100
        } else { // return simple average if reference columns not found
          conv_rate = this.averageItems(items)
        }
        return this.roundData(conv_rate, key) + '%'
      } else if (['cost_per_conversion'].includes(key)) {
        var cost_per_conv
        if (this.keys.includes('cost') && this.keys.includes('conversions')) {
          cost = this.aggregateColumn('cost')
          cost_per_conv = Number(cost.replace(/[^0-9.-]+/g,"")) / this.aggregateColumn('conversions')
        } else { // return simple average if reference columns not found
          cost_per_conv = this.averageItems(items)
        }
        return this.roundData(cost_per_conv, key)
      }
    },

    formatSeconds (seconds) {
      var result
      if (!isNaN(seconds) && seconds !== null) {
        if (seconds.toString().indexOf('.') != -1) {
          try {
            result = seconds.toFixed()
          }
          catch(err) {
            result = parseFloat(seconds).toFixed()
          }
        }
      } else {
        result = seconds
      }
      return Moment().startOf('day').seconds(result).format('HH:mm:ss')
    },

    sumItems (items) {
      return Object.values(items).reduce((a, b) => a + b)
    },

    averageItems (items) {
      items = items.filter(function (item) {
        return item != null && !isNaN(item)
      })
      if (items.length < 1) {
        return '< 10'
      } else {
        var sum = Object.values(items).reduce((a, b) =>  a + b)
        return sum / items.length
      }
    },

    checkNumber (value) {
      if ((typeof value === 'string' || value instanceof String) && (value.includes('%') || value.includes('$') || (/[a-zA-Z]/g.test(value) === false && value.includes(':')))) {
        return true
      } else {
        return false
      }
    }
  },

  computed: {
    keys () {
      return this.fields.map(function(field){
        return field.key
      })
    }
  }

}
</script>

<style scoped>
.table-scroll-btn{
  position: absolute;
  z-index: 12;
  right: 3%;
  bottom: 3%;
}

.table-container {
  height: 100%;
}

.card{
  margin-bottom: 0;
  overflow: auto;
  height: inherit;
}

.no-paddings {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: opacity 0.8s;
}
.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
}
.loading-text {
  color: #757575;
  text-align: center;
  margin-top: 10px;
  text-transform: uppercase;
  font-family: 'Cooper Hewitt Semibold';
  letter-spacing: 3px;
}
.table-count {
  margin-left: 10px;
  color: #757575;
}
</style>
